import React from 'react';

/**
 * Tiny inline SVG sparkline. No dependency on recharts (keeps it lightweight).
 *
 * Props:
 *   data: [{ date: "YYYY-MM-DD", count: number }, ...]  (chronological order)
 *   color: stroke color
 *   width / height: px
 */
export default function Sparkline({ data = [], color = '#2563EB', width = 100, height = 28 }) {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d.count || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;

  const stepX = data.length > 1 ? width / (data.length - 1) : width;

  const points = values.map((v, i) => {
    const x = i * stepX;
    // invert Y: higher values go up (smaller y)
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  // Area fill polygon (closes at the bottom)
  const areaPoints = `0,${height} ${points} ${((values.length - 1) * stepX).toFixed(1)},${height}`;
  const lastValue = values[values.length - 1];
  const prevValue = values[values.length - 2] ?? lastValue;
  const trend = lastValue > prevValue ? 'up' : lastValue < prevValue ? 'down' : 'flat';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      data-testid="sparkline"
      data-trend={trend}
      role="img"
      aria-label={`Évolution ${data.length} derniers jours, dernière valeur ${lastValue}`}
    >
      <polygon points={areaPoints} fill={color} fillOpacity="0.08" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {/* Last point marker */}
      <circle
        cx={((values.length - 1) * stepX).toFixed(1)}
        cy={(height - ((lastValue - min) / range) * (height - 4) - 2).toFixed(1)}
        r="2.5"
        fill={color}
      />
    </svg>
  );
}
